<template lang="pug">
component(
  :data-id="id || null"
  :data-text="text"
  :data-key="k"
  :id="id || null"
  :is="type"
  :name="name"
  v-html="html"
)
</template>

<script setup lang="ts">
import getValue from 'get-value';
import renderMarkdown from '../lib/renderMarkdown';
import useSuspend from '../lib/useSuspend';
import { computed, getCurrentInstance, unref } from 'vue';
import useTranslations from '../stores/useTranslations';
import type { ComponentInternalInstance } from 'vue';
import { inflate } from '../lib/textInflation';

const { translate: getTranslation } = useTranslations();
const suspend = useSuspend();

const props = defineProps({
  text: { type: String, default: '' },
  id: { type: String, default: '' },
  name: { type: String, default: '' },
  default: { type: String, default: '' },
  keepOneParagraph: { type: Boolean, default: false },
  type: { type: String, default: 'span' },
  k: { type: String, default: null },
  values: { type: Object, default: () => ({}) },
  translating: { type: Boolean, default: false }
});

const html = computed(() => {
  const md = inflate(fillTemplates(translate(props.k, props.text)));
  const str = renderMarkdown(md, { keepOneParagraph: props.keepOneParagraph });

  return str;
});

const isValue = (v: unknown) => typeof v === 'string' || typeof v === 'number';

/**
 * Replaces "${nested.property}" with value of app.suspend.nested.property
 *
 * @param str
 */
function fillTemplates(str: string) {
  const templates: string[] = str.match(/\$\{[^}]+\}/g) || [];
  const values = props.values as Record<string, unknown>;

  templates.forEach(m => {
    try {
      const [path, defaultValue] = m.slice(2, -1).split(/, */);
      let value: string | number | undefined;
      const fromProps = getValue(values, path);

      if (isValue(fromProps)) {
        value = fromProps;
      } else {
        // use internal Vue APIs to access parent instances
        type Parent = ComponentInternalInstance & {
          ctx?: object;
          provides?: object;
        };

        let parent: Parent = getCurrentInstance();
        let tries = 10;

        // search through parent components' data for a truthy value
        while (parent && !isValue(value) && tries--) {
          parent = parent.parent;
          value = unref(
            getValue(parent?.ctx, path) ||
              getValue(parent?.exposed, path) ||
              getValue(parent?.provides, path)
          );
        }

        if (!isValue(value)) {
          if (defaultValue) {
            value = defaultValue;
          } else {
            throw new Error(
              `${path} is not defined or null and cannot be replaced in a placeholder.`
            );
          }
        }
      }

      str = str.replace(m, translate(value));
    } catch (e) {
      console.error(e);
    }
  });

  return str;
}

/**
 * Returns the value found at the given key of the current translation, or a given fallback, which defaults to the key itself
 * @param key
 * @param fallback
 */
function translate(key: string | number, fallback: string | number = key) {
  return getTranslation(suspend.locale, key, fallback);
}
</script>
